import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { CustomEvents, DEFAULT_PRECISION, FREE_PLAN_LEVEL, FREQUENCY_TO_DAYS, LEGIT_PLAN_THREASHOLD, MAX_SAFE_INTEGER, MAXIMUM_MULTIPLIER_UNLIMITED, Utils, ZERO_PLAN_LEVEL } from '@app/_helpers';
import { ConfigurationService } from '@app/_shared/providers/configuration/configuration.service';
import { EventsService } from '@app/_shared/providers/events/events.service';
import { NgxToastrService } from '@app/_shared/providers/ngx-toastr/ngx-toastr.service';
import { BooksSocketService } from '@app/_shared/providers/websocket/books-socket/books-socket.service';
import { SubjectType } from '@app/_shared/providers/websocket/websocket.enums';
import { FREE_PLAN_VALIDITY } from '@app/_shared/strings';
import { Exchange } from '@app/app.structs';
import { AlgoPermissionMapping } from '@app/modules/admin/components/plan-n-pricing/components/plan-pricing-detail/components/algo-permissions-form/algo-permissions-mapping';
import { Subscription } from 'rxjs';
import { PlanSubscription } from '../profile/components/plans/plans.enums';
import { SubscriptionStatus } from './components/plans-status/plans-status.constants';
import { SubscriptionMessages } from './providers/mb-plans.constants';
import { CatalogPlan, CurrentPlan, PlanPricing, SubscriptionState, UserDetails } from './providers/mb-plans.interface';
import { MbPlansService } from './providers/mb-plans.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-mb-plans',
  templateUrl: './mb-plans.component.html',
  styleUrls: ['./mb-plans.component.scss']
})
export class MbPlansComponent implements OnInit, OnDestroy {

  isDialog: boolean = false;
  private subscriptions = new Subscription();
  loading = false;

  freePlanValidityText: string;
  showNewPlanCard: boolean = false;
  plansOfferUrls: string[];
  frequency_map: Record<string, string>;
  selectedTab: string;

  customBacktestAllowed: boolean = false;

  currentPlan: CurrentPlan;
  userDetails: UserDetails;
  downgradedPlanId: number;
  isExpired: boolean = false;


  plansList: CatalogPlan[] = [];

  promotionPlanPricingIds: Set<number> = new Set();

  showNseEllipsis: boolean = true;
  showBseEllipsis: boolean = true;
  planSymbolsMap: Map<number, Map<string, string>> = new Map();

  mostPopularPlanId: number;
  defaultPlanId: number;
  frequencyArray: string[] = [];

  /** Pricing id for which subscribe is in progress (disables that button) */
  subscribingPricingId: number | null = null;
  showStatusScreen = false;
  currentStatus: SubscriptionStatus = SubscriptionStatus.REDIRECTING;

  MAX_SAFE_INTEGER = MAX_SAFE_INTEGER;
  DEFAULT_PRECISION = DEFAULT_PRECISION;
  MAXIMUM_MULTIPLIER_UNLIMITED = MAXIMUM_MULTIPLIER_UNLIMITED;
  Exchange = Exchange;
  getDiscountedPrice = Utils.getDiscountedPrice;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) private planDialogData,
    private dialogRef: MatDialogRef<MbPlansComponent>,
    private eventsService: EventsService,
    private configService: ConfigurationService,
    private socketService: BooksSocketService,
    private mbPlansService: MbPlansService,
    private toastr: NgxToastrService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.subscriptions.add(
      this.socketService.getSubject(SubjectType.SUBSCRIPTION).subscribe((res) => {
        this.fetchUserDetails(true);
      })
    );
  }

  private fetchUserDetails(update: boolean = false) {
    this.getCurrentPlan(update);
    this.getUserDetails(update);
  }

  ngOnInit(): void {
    this.isDialog = !!this.planDialogData;
    this.showNewPlanCard = this.configService.remoteConfigCacheObj?.NEW_PLAN_CARD;
    this.freePlanValidityText = this.configService.remoteConfigCacheObj?.FREE_PLAN_VALIDITY || FREE_PLAN_VALIDITY;
    this.frequency_map = this.configService.remoteConfigCacheObj?.FREQUENCY_NAME_MAPPING;
    this.customBacktestAllowed = this.configService.remoteConfigCacheObj?.BACKTEST_EXCHANGE_PERMISSIONS?.NSEFO_BACKTEST;

    const offerUrlsString = this.configService.remoteConfigCacheObj?.PLAN_OFFER_URLS;
    if (offerUrlsString) {
      this.plansOfferUrls = offerUrlsString.split(',').map(url => url.trim());
    }

    this.fetchUserDetails();
    this.getPlansCatalog();

    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        const id = params['id'];
        if (id) {
          this.showStatusScreen = true;
          this.startSubscriptionVerification(id);
          this.router.navigate([], {
            queryParams: { state: undefined, id: undefined },
            queryParamsHandling: 'merge',
          });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getCurrentPlan(update: boolean = false) {
    this.mbPlansService.getCurrentPlan(update).subscribe(currentPlan => {
      this.currentPlan = currentPlan;
      this.isExpired = (currentPlan.plan.level === ZERO_PLAN_LEVEL)
      this.eventsService.publish(CustomEvents.PlanChanged, update);
    })
  }

  getUserDetails(update: boolean = false) {
    this.mbPlansService.getUserDetails(update).subscribe(details => {
      this.userDetails = details;
      this.downgradedPlanId = details.next_plan_id;
    })
  }

  getPlansCatalog() {
    this.loading = true;
    this.mbPlansService.getPlansCatalog().subscribe(res => {
      if (res.success && res.data) {
        this.plansList = res.data?.plans || [];

        this.plansList.forEach(plan => {
          plan.pricing_options.forEach(pricing => {
            if (pricing.is_promotion_plan) {
              this.promotionPlanPricingIds.add(pricing.id);
            }
          })
        });

        this.processData();
      }
    })
  }

  async processData() {
    this.userDetails = await this.mbPlansService.getUserDetails(true).toPromise();

    this.plansList.sort((a, b) => a.level - b.level);
    this.plansList.forEach(plan => {
      const symbolsMap = Utils.getSymbolsForPlan(plan);
      this.planSymbolsMap.set(plan.id, symbolsMap);
    });

    this.mostPopularPlanId = this.plansList.find(plan => plan.most_popular)?.id;
    this.defaultPlanId = this.plansList.find(plan => plan.is_default)?.id;

    this.sortFrequencyArray();
    this.selectedTab = this.frequencyArray[0];
    if (this.currentPlan?.plan?.level !== ZERO_PLAN_LEVEL) {
      const candidateTab = this.getFrequencyValue(this.currentPlan?.plan?.duration, this.currentPlan?.plan?.frequency);
      if (this.frequencyArray.includes(candidateTab)) {
        this.selectedTab = candidateTab;
      }
    }

    this.loading = false;

    const planId = this.planDialogData?.planId;
    if (planId) {
      const plan = this.plansList.find((p) => p.id === planId);
      if (plan
        && plan.id === this.currentPlan?.plan?.id
        && plan.id !== this.defaultPlanId
        && !(this.currentPlan?.subscription_status === PlanSubscription.INACTIVE || this.currentPlan?.subscription_status === PlanSubscription.CANCELLED)
      ) {
        this.subscribePlan(plan.pricing_options[0].id);
      }
    }
  }

  isCurrentPlan(plan: CatalogPlan, pricing: PlanPricing) {
    if (!this.currentPlan) return false;
    return this.currentPlan.plan?.id === plan?.id && this.currentPlan.pricing?.id === pricing?.id;
  }

  sortFrequencyArray(): void {
    this.frequencyArray = [];

    if (!this.frequency_map) {
      return;
    }

    const seenKeys = new Set<string>();
    const combined: { frequency: string; day: number }[] = [];

    for (const plan of this.plansList) {
      if (plan.level === FREE_PLAN_LEVEL) continue;

      for (const pricing of plan.pricing_options ?? []) {
        const frequencyKey = `${pricing.duration}_${pricing.frequency ?? ''}`;
        if (seenKeys.has(frequencyKey)) continue;

        const frequency = this.frequency_map[frequencyKey];
        if (!frequency) continue;

        const days = pricing.duration * (FREQUENCY_TO_DAYS[pricing.frequency] ?? 0);
        seenKeys.add(frequencyKey);
        combined.push({ frequency, day: days });
      }
    }

    combined.sort((a, b) => a.day - b.day);
    this.frequencyArray = combined.map(c => c.frequency);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  getFrequencyValue(duration: number, frequency: string) {
    const frequency_key = `${duration}_${frequency}`;
    return this.frequency_map[frequency_key];
  }

  getUTradeIntelligenceTick(plan: CatalogPlan): string {
    return plan.algo_permissions.includes(AlgoPermissionMapping['uTrade ONE AI']) ? 'tick' : 'cancel';
  }

  getuTradeOriginalAllowed(plan: CatalogPlan) {
    return plan.algo_permissions.indexOf(AlgoPermissionMapping['uTrade Originals']) > -1;
  }

  getTick(plan: CatalogPlan, liveTrading: boolean): string {
    return liveTrading || (plan.level < LEGIT_PLAN_THREASHOLD) ? 'tick' : 'cancel';
  }

  getMarketCount(plan: CatalogPlan): string {
    let marketString = '';
    const marketCounts = plan.metadata.count;

    for (const [market, count] of Object.entries(marketCounts)) {
      if (count > 0) {
        if (marketString) {
          marketString += ' + ';
        }
        marketString += `${count} ${market.split('_')[0].toUpperCase()}`;
      }
    }

    return marketString || '--';
  }

  getPlanSymbols(planId: number, exchange: Exchange): string {
    return this.planSymbolsMap?.get(planId)?.get(exchange) || '';
  }

  /**
   * Whether to show the plan in the list. FREE plan is hidden when current plan is
   * ZERO_PLAN_LEVEL or any plan other than FREE_PLAN_LEVEL (i.e. show FREE only when user is on free plan).
   */
  shouldShowPlan(plan: CatalogPlan): boolean {
    if (plan.level !== FREE_PLAN_LEVEL) {
      return true;
    }
    const currentLevel = this.currentPlan?.plan?.level;
    return currentLevel === FREE_PLAN_LEVEL;
  }

  isPlanDowngradable(level: number, pricing: PlanPricing) {
    const currentPlanTotalDuration = this.currentPlan?.pricing.duration * FREQUENCY_TO_DAYS[this.currentPlan?.pricing.frequency];
    const totalDuration = pricing.duration * FREQUENCY_TO_DAYS[pricing?.frequency];
    return this.currentPlan?.plan.level !== FREE_PLAN_LEVEL && this.currentPlan?.plan.level !== ZERO_PLAN_LEVEL
      && (level < this.currentPlan?.plan.level || totalDuration < currentPlanTotalDuration);
  }

  toggleEllipsis(isNSE: boolean, el: HTMLElement) {
    if (isNSE) {
      this.showNseEllipsis = !this.showNseEllipsis;
    } else {
      this.showBseEllipsis = !this.showBseEllipsis;
    }
    el.scrollLeft -= el.scrollLeft;
  }

  subscribePlan(pricingId: number) {
    this.subscribingPricingId = pricingId;

    // Show redirecting status immediately
    this.showStatusScreen = true;
    this.currentStatus = SubscriptionStatus.REDIRECTING;

    this.mbPlansService.subscribePlan(pricingId).subscribe({
      next: (res) => {
        if (!res?.success) {
          // Show failure status
          this.currentStatus = SubscriptionStatus.FAILURE;
          this.toastr.simpleCustomToaster(res?.error || SubscriptionMessages.CheckoutFailure);
          return;
        }

        const hostedPageUrl = res.data?.result?.hosted_page?.url;
        const isDowngrade = res.data?.isDowngrade === true;

        if (hostedPageUrl) {
          window.location.href = hostedPageUrl;
          return;
        }

        if (isDowngrade) {
          const hasScheduledChanges = res?.data?.result?.subscription?.['has_scheduled_changes'] === true;

          // Show success for downgrade
          // this.toastr.simpleCustomToaster(
          //   hasScheduledChanges ? SubscriptionMessages.PlanChangeScheduled : SubscriptionMessages.PlanChangeApplied
          // );
          
          // Redirect back to plans after success
          this.showDowngradeSuccess();
          return;
        }

        // If we reach here, something went wrong
        this.currentStatus = SubscriptionStatus.FAILURE;
        this.toastr.simpleCustomToaster(res?.error || SubscriptionMessages.CheckoutFailure);
      },
      error: (err) => {
        // Show failure status
        this.currentStatus = SubscriptionStatus.FAILURE;
        const message = err?.error?.error || err?.error?.message || SubscriptionMessages.GenericError;
        this.toastr.simpleCustomToaster(message);
      },
    });
  }

  startSubscriptionVerification(hostedPageId: string): void {
    // Poll for subscription status
    this.currentStatus = SubscriptionStatus.VERIFYING;
    const pollInterval = setInterval(() => {
      this.mbPlansService.getPlanSubscriptionStatus(hostedPageId).subscribe({
        next: (res) => {
          if (res?.success && res?.data?.state) {
            if (res?.data?.state === SubscriptionState.Succeeded) {
              clearInterval(pollInterval);
              this.showSubcriptionSuccess();
            } else if (res?.data?.state === SubscriptionState.Cancelled || res?.data?.state === SubscriptionState.Failed) {
              clearInterval(pollInterval);
              this.currentStatus = SubscriptionStatus.FAILURE;
            }
          }
        },
        error: (_) => {
          clearInterval(pollInterval);
          this.currentStatus = SubscriptionStatus.FAILURE;
        }
      });
    }, 5000);

    setTimeout(() => {
      clearInterval(pollInterval);
      if (this.currentStatus === SubscriptionStatus.VERIFYING) {
        this.currentStatus = SubscriptionStatus.FAILURE;
        this.toastr.simpleCustomToaster('Subscription verification timeout. Please refresh the page.');
      }
    }, 300000); // 5 minutes
  }

  resetStatusScreen(): void {
    this.showStatusScreen = false;
    this.subscribingPricingId = null;
    this.currentStatus = SubscriptionStatus.REDIRECTING;
  }

  showSubcriptionSuccess() {
    this.currentStatus = SubscriptionStatus.SUCCESS;
    setTimeout(() => {
      this.resetStatusScreen();
      this.fetchUserDetails(true);
    }, 3000);
  }

  showDowngradeSuccess() {
    this.currentStatus = SubscriptionStatus.DOWNGRADE;
    setTimeout(() => {
      this.resetStatusScreen();
      this.fetchUserDetails(true);
    }, 3000);
  }
}
